// src/components/Town/utils/fetchProjectionTables.js

import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";

const EMPTY_RESULT = {
  currentYear: null,
  growthRatePct: null,
  selectedWindow: [],
  operatingData: [],
  fundsData: [],
  ufbData: [],
};

export async function fetchProjectionTables(selectedYear) {
  // ----- BASIC GUARD -----
  if (!selectedYear) {
    console.warn("[Projections] No selectedYear, returning EMPTY_RESULT");
    return EMPTY_RESULT;
  }

  // "FY2025 - 2026" → currentYear = 2026
  const match = selectedYear.match(/FY(\d{4})\s*-\s*(\d{4})/);
  const currentYear = match
    ? parseInt(match[2], 10)
    : new Date().getFullYear() + 1;

  if (!currentYear) {
    console.warn("[Projections] Could not parse currentYear from", selectedYear);
    return EMPTY_RESULT;
  }
  // ----- LOAD DOC -----
  // Adjust this path if your doc actually lives at collection "Projections"/doc "Projections"
  const snap = await getDoc(doc(db, "townGovernment", "Projections"));
  if (!snap.exists()) {
    console.warn("[Projections] townGovernment/Projections doc does not exist");
    return EMPTY_RESULT;
  }

  const sharedData = snap.data();
  const topLevelKeys = Object.keys(sharedData);

  // ----- CHOOSE WHICH YEAR BLOCK TO USE -----
  // {
  //   "2020": { years: { "2018_2019": {...}, ... } },
  //   "2021": { years: { "2020_2021": {...}, ... } },
  //   ...
  // }
  const numericYearKey = String(currentYear);

  const availableYearBlocks = topLevelKeys
    .filter((k) => /^\d{4}$/.test(k) && sharedData[k] && sharedData[k].years);

  if (!availableYearBlocks.length) {
    console.warn("[Projections] No numeric year blocks with .years found in doc");
    return EMPTY_RESULT;
  }

  let chosenYearKey;
  if (availableYearBlocks.includes(numericYearKey)) {
    chosenYearKey = numericYearKey;
  } else {
    const sortedYears = availableYearBlocks
      .map((y) => parseInt(y, 10))
      .sort((a, b) => a - b);

    const lowerOrEqual = sortedYears.filter((y) => y <= currentYear);
    const fallbackYear =
      lowerOrEqual.length > 0
        ? lowerOrEqual[lowerOrEqual.length - 1]
        : sortedYears[sortedYears.length - 1];

    chosenYearKey = String(fallbackYear);
  }

  const yearBlock = sharedData[chosenYearKey]?.years || {};
  const allYears = Object.keys(yearBlock);

  if (!allYears.length) {
    console.warn(
      "[Projections] chosenYearKey has no .years entries:",
      chosenYearKey,
      yearBlock
    );
    return EMPTY_RESULT;
  }

  // ----- GLOBAL GROWTH RATE -----
  let growthRatePct = 3.5;
  for (const label of allYears) {
    const maybeRate = yearBlock[label]?.meta?.growthRatePct;
    if (typeof maybeRate === "number") {
      growthRatePct = maybeRate;
      break;
    }
  }

  // ----- BUILD SELECTED WINDOW OF FY PAIRS -----
  // We want the same pattern as buildProjectionColumns:
  //  one "Actuals" year (currentYear - 3 / -2)
  //  plus 5 projected years (currentYear - 2 .. currentYear + 2)
  //
  // e.g. currentYear = 2025 → window =
  //   ["2022_2023", "2023_2024", "2024_2025", "2025_2026", "2026_2027", "2027_2028"]

  const selectedWindow = [];

  for (let offset = -3; offset <= 2; offset += 1) {
    const fyStart = currentYear + offset;
    const fyEnd = fyStart + 1;
    selectedWindow.push(`${fyStart}_${fyEnd}`);
  }
  // Fallback: if nothing matched (shouldn't happen, but safe), just use all years sorted
  if (!selectedWindow.length) {
    selectedWindow.push(
      ...[...allYears].sort((a, b) => {
        const aStart = parseInt(a.split("_")[0], 10);
        const bStart = parseInt(b.split("_")[0], 10);
        return aStart - bStart;
      })
    );
  }

  if (!selectedWindow.length) {
    console.warn(
      "[Projections] No FY ranges in window for selectedYear/currentYear:",
      selectedYear,
      currentYear
    );
    return {
      currentYear,
      growthRatePct,
      selectedWindow,
      operatingData: [],
      fundsData: [],
      ufbData: [],
    };
  }

  // ----- HELPERS -----
  const getOpsMapForYear = (yearLabel) => {
    const yearData = yearBlock[yearLabel] || {};
    const arr = Array.isArray(yearData.operatingResults)
      ? yearData.operatingResults
      : [];
    const map = {};
    for (const row of arr) {
      if (!row || !row.key) continue;
      map[row.key] = row;
    }
    return map;
  };

  const getFundsMapForYear = (yearLabel) => {
    const yearData = yearBlock[yearLabel] || {};
    const arr = Array.isArray(yearData.funds) ? yearData.funds : [];
    const map = {};
    for (const row of arr) {
      if (!row || !row.key) continue;
      map[row.key] = row;
    }
    return map;
  };

  const getMetaForYear = (yearLabel) => {
    const yearData = yearBlock[yearLabel] || {};
    const meta = yearData.meta || {};
    return {
      ufbAsPercentOfExpenditures: meta.ufbAsPercentOfExpenditures ?? null,
      growthRatePct: meta.growthRatePct ?? null,
      type: meta.type || null,
    };
  };

  // ----- OPERATING RESULTS TABLE -----
  const sortedYearLabels = [...allYears].sort((a, b) => {
    const aStart = parseInt(a.split("_")[0], 10);
    const bStart = parseInt(b.split("_")[0], 10);
    return aStart - bStart;
  });

  const templateOpsYear = sortedYearLabels[0];
  const templateOpsArr =
    Array.isArray(yearBlock[templateOpsYear]?.operatingResults) &&
    yearBlock[templateOpsYear].operatingResults.length
      ? [...yearBlock[templateOpsYear].operatingResults].sort(
          (a, b) =>
            (typeof a.order === "number" ? a.order : 0) -
            (typeof b.order === "number" ? b.order : 0)
        )
      : [];

  const operatingRowDefs = templateOpsArr.map((row) => ({
    key: row.key,
    label: row.label,
    order: typeof row.order === "number" ? row.order : 0,
    bold: !!row.bold,
  }));

  let operatingData = operatingRowDefs
    .map((def) => {
      const row = {
        label: def.label,
        order: def.order,
        bold: def.bold,
      };

      for (const yearLabel of selectedWindow) {
        const opsMap = getOpsMapForYear(yearLabel);
        const match = opsMap[def.key];
        const val =
          match && typeof match.value === "number" ? match.value : null;
        row[yearLabel] = val;
      }

      return row;
    })
    .sort((a, b) => a.order - b.order);

  // ----- EXPENDITURE ESTIMATE GROWTH + FOOTER ROW -----

  // Find the "Expenditure Actuals & Estimates" row (case-insensitive, prefix match)
  const expIndex = operatingData.findIndex(
    (r) =>
      typeof r.label === "string" &&
      r.label.toLowerCase().startsWith("expenditure actuals")
  );

  if (expIndex !== -1) {
    const expRow = { ...operatingData[expIndex] };

    // 1) apply growth from the 4th value onward (index 3)
    const rate = typeof growthRatePct === "number" ? growthRatePct / 100 : 0.035;

    const values = selectedWindow.map((label) =>
      typeof expRow[label] === "number" ? expRow[label] : null
    );

    if (values.filter((v) => typeof v === "number").length >= 3 && rate > 0) {
      for (let i = 3; i < values.length; i += 1) {
        const prev = values[i - 1];
        if (typeof prev === "number") {
          const nextVal = Math.round(prev * (1 + rate));
          values[i] = nextVal;
        }
      }

      selectedWindow.forEach((label, idx) => {
        const v = values[idx];
        expRow[label] = typeof v === "number" ? v : null;
      });
    }

    operatingData[expIndex] = expRow;

    // 2) build footer row: "ADOPTED BUDGET" / "Estimated @ X%"
    // This row is rendered specially in CommonTable (merged cells).
    const footerRow = {
      label: "", // label cell will be blank
      order: expRow.order + 0.1,
      bold: false,
      footerType: "projectionFootnote",
      growthRatePct, // so CommonTable can show "Estimated @ X%"
    };

    // insert the footer directly after the expenditure row
    operatingData.splice(expIndex + 1, 0, footerRow);
  }

  // ----- FUNDS TABLE -----
  const templateFundsYear =
    sortedYearLabels.find(
      (label) =>
        Array.isArray(yearBlock[label]?.funds) &&
        yearBlock[label].funds.length
    ) || sortedYearLabels[0];

  const templateFundsArr =
    Array.isArray(yearBlock[templateFundsYear]?.funds) &&
    yearBlock[templateFundsYear].funds.length
      ? [...yearBlock[templateFundsYear].funds].sort(
          (a, b) =>
            (typeof a.order === "number" ? a.order : 0) -
            (typeof b.order === "number" ? b.order : 0)
        )
      : [];

  const fundRowDefs = templateFundsArr.map((item) => ({
    key: item.key,
    label: item.label,
    order: typeof item.order === "number" ? item.order : 0,
    bold: !!item.bold,
  }));

  const fundsData = fundRowDefs
    .map((def) => {
      const row = {
        label: def.label,
        order: def.order,
        bold: def.bold,
      };

      for (const yearLabel of selectedWindow) {
        const fundsMap = getFundsMapForYear(yearLabel);
        const match = fundsMap[def.key];
        const val =
          match && typeof match.value === "number" ? match.value : null;
        row[yearLabel] = val;
      }

      return row;
    })
    .map((row) => {
      const upper = (row.label || "").toUpperCase();
      if (upper.startsWith("TOTAL FUND BALANCE")) row.order = -100;
      if (
        upper.startsWith("UNASSIGNED FUND BALANCE JUNE30") ||
        upper.startsWith("UNASSIGNED FUND BALANCE (UFB) - JUNE 30")
      ) {
        row.order = 1000;
      }
      return row;
    })
    .sort((a, b) => a.order - b.order);

  // ----- UFB BENCHMARK TABLE -----
  const ufbRow = {
    label: "UFB as % of Expenditures",
    order: 0,
  };

  for (const yearLabel of selectedWindow) {
    const meta = getMetaForYear(yearLabel);
    const val = meta.ufbAsPercentOfExpenditures;
    ufbRow[yearLabel] = typeof val === "number" ? val : null;
  }

  const ufbData = [ufbRow];

  return {
    currentYear,
    growthRatePct,
    selectedWindow,
    operatingData,
    fundsData,
    ufbData,
  };
}
